// services/documentService.js

const API_HOST = process.env.REACT_APP_BACKEND_API_HOST || 'http://localhost:8080';


/**
 * Service for handling document upload and management API calls
 */
const documentService = (apiClient) => ({
  /**
   * Upload documents for translation
   * @param {FormData} formData - FormData object containing files and metadata
   * @returns {Promise<Object>} - Quote information
   */
  uploadDocuments: async (formData) => {
    try {
      // Set content type to multipart/form-data for file uploads
      const response = await apiClient.post('/documents/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading documents:', error);
      throw error;
    }
  },

  /**
   * Process payment for a translation quote
   * @param {string} quoteId - ID of the quote to process payment for
   * @returns {Promise<Object>} - Payment confirmation
   */
  processPayment: async (quoteId) => {
    try {
      const response = await apiClient.post(`/documents/${quoteId}/payment`);
      return response.data;
    } catch (error) {
      console.error(`Error processing payment for quote ${quoteId}:`, error);
      throw error;
    }
  },

  /**
   * Get document details by ID
   * @param {string} documentId - ID of the document to retrieve
   * @returns {Promise<Object>} - Document object
   */
  getDocumentById: async (documentId) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching document ${documentId}:`, error);
      throw error;
    }
  },

  /**
   * Get translated DOCX for a document
   * @param {string} documentId - ID of the document
   * @returns {Promise<Object>} - { success: true, docx: base64String, filename: string }
   */
  getTranslatedDocx: async (documentId) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/docx`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching translated DOCX for document ${documentId}:`, error);
      throw error;
    }
  },

  /**
   * Get all documents for a user by email
   * @param {string} email - Email of the user
   * @returns {Promise<Array>} - Array of document objects
   */
  getDocumentsByUser: async (email) => {
    try {
      const response = await apiClient.get('/documents', { params: { email } });
      return response.data;
    } catch (error) {
      console.error('Error fetching user documents:', error);
      throw error;
    }
  },

  /**
   * Upload a document file for translation
   * @param {File} file - The document file to upload
   * @returns {Promise<Object>} - Document upload response with document ID
   */
  uploadDocument: async (file, projectId) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      
      const response = await apiClient.post(`/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  },

  /**
   * Trigger translation for a document
   * @param {string} documentId - ID of the document to translate
   * @param {Object} translationOptions - Options for translation
   * @returns {Promise<Object>} - Translation job response
   */
  triggerTranslation: async (documentId, translationOptions = {}) => {
    try {
      const response = await apiClient.post(`/documents/${documentId}/translate`, translationOptions);
      return response.data;
    } catch (error) {
      console.error(`Error triggering translation for document ${documentId}:`, error);
      throw error;
    }
  },

  /**
   * Check translation status for a document
   * @param {string} documentId - ID of the document
   * @returns {Promise<Object>} - Translation status response
   */
  checkTranslationStatus: async (documentId) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/status`);
      return response.data;
    } catch (error) {
      console.error(`Error checking translation status for document ${documentId}:`, error);
      throw error;
    }
  },

  /**
   * Get translated HTML files for a document
   * @param {string} documentId - ID of the document
   * @returns {Promise<Object>} - HTML files response
   */
  getTranslatedHTMLs: async (documentId) => {
    try {
      const response = await apiClient.get(`/documents/${documentId}/htmls`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching translated HTMLs for document ${documentId}:`, error);
      throw error;
    }
  },

  getOriginalDocumentPreviewUrl: (documentId) => {
    return `${API_HOST}/documents/${documentId}/original?preview=true`;
  },

  // services/documentService.js
  improveHTMLWithSelection: async (documentId, improveRequest) => {
    try {
      const response = await apiClient.post(`/documents/${documentId}/improve-html`, {
        currentHtml: improveRequest.currentHtml,
        userFeedback: improveRequest.userFeedback,
        selectedHtml: improveRequest.selectedHtml,
        selectedText: improveRequest.selectedText,
        selectionContext: improveRequest.selectionContext,
        hasSelection: improveRequest.hasSelection,
        isCommand: improveRequest.isCommand,
        commandType: improveRequest.commandType,
        conversationHistory: improveRequest.conversationHistory || []
      });

      return response.data;
    } catch (error) {
      console.error(`Error improving HTML with selection for document ${documentId}:`, error);
      throw error;
    }
  },

  /**
   * Save Google Doc ID for a document
   * @param {string} documentId - ID of the document
   * @param {string} googleDocId - Google Doc ID to save
   * @returns {Promise<Object>} - Success response
   */
  saveGoogleDocId: async (documentId, googleDocId) => {
    try {
      const response = await apiClient.post(`/documents/${documentId}/google-doc-id`, {
        googleDocId
      });
      return response.data;
    } catch (error) {
      console.error(`Error saving Google Doc ID for document ${documentId}:`, error);
      throw error;
    }
  },

  /**
   * Send the final document to a client by email
   * @param {string} projectId - ID of the project
   * @param {string} clientEmail - Recipient email address
   * @param {string} language - Email language ("en" or "fr")
   * @param {boolean} includeDocuments - Whether to include project documents
   * @param {number|null} version - Optional final document version to send; defaults to latest when omitted
   * @param {Object} options
   * @param {boolean|undefined} options.includeCombinedFinal - Whether to attach the combined final PDF
   * @param {string[]} options.translationFinalDocumentIds - Per-document signed PDF IDs to attach
   * @returns {Promise<Object>} - { success, message, details }
   */
  sendFinalDocumentToClient: async (projectId, clientEmail, language = 'fr', includeDocuments = false, version = null, options = {}) => {
    try {
      const response = await apiClient.post(`/projects/${projectId}/final-documents/send-to-client`, {
        clientEmail,
        language,
        includeDocuments,
        ...(version ? { version } : {}),
        ...(options.includeCombinedFinal !== undefined ? { includeCombinedFinal: options.includeCombinedFinal } : {}),
        ...(Array.isArray(options.translationFinalDocumentIds) ? { translationFinalDocumentIds: options.translationFinalDocumentIds } : {}),
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending final document for project ${projectId}:`, error);
      throw error;
    }
  },

  uploadTranslationFinalDocument: async (projectId, translationId, pdfBlob, fileName, documentHash, googleDocTitle = null) => {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfBlob, fileName);
      formData.append('documentHash', documentHash);
      if (googleDocTitle) {
        formData.append('googleDocTitle', googleDocTitle);
      }
      const response = await apiClient.post(`/projects/${projectId}/translations/${translationId}/final-documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading final document for translation ${translationId}:`, error);
      throw error;
    }
  },

  uploadTranslationFinalDocumentWithCertificate: async (
    projectId,
    translationId,
    pdfBlob,
    fileName,
    certificateFile,
    certificatePassword,
    options = {}
  ) => {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfBlob, fileName);
      formData.append('certificate', certificateFile, certificateFile?.name || 'signer-certificate.p12');
      formData.append('certificatePassword', certificatePassword || '');

      if (options.documentHash) formData.append('documentHash', options.documentHash);
      if (options.signerDisplayName) formData.append('signerDisplayName', options.signerDisplayName);
      if (options.signerContactInfo) formData.append('signerContactInfo', options.signerContactInfo);
      if (options.signerTitle) formData.append('signerTitle', options.signerTitle);
      if (options.signatureReason) formData.append('signatureReason', options.signatureReason);
      if (options.signatureLocation) formData.append('signatureLocation', options.signatureLocation);
      if (options.certificateAnchor) formData.append('certificateAnchor', JSON.stringify(options.certificateAnchor));
      if (options.certificateAppearance) formData.append('certificateAppearance', JSON.stringify(options.certificateAppearance));
      if (options.signatureAppearanceImage) formData.append('signatureAppearanceImage', options.signatureAppearanceImage);
      if (options.googleDocTitle) formData.append('googleDocTitle', options.googleDocTitle);

      const response = await apiClient.post(`/projects/${projectId}/translations/${translationId}/final-documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading digitally signed final document for translation ${translationId}:`, error);
      throw error;
    }
  },

  /**
   * Upload additional project documents
   * @param {string} projectId - ID of the project
   * @param {File[]} files - Files to upload
   * @returns {Promise<Object>} - Upload response with created document rows
   */
  uploadProjectDocuments: async (projectId, files = []) => {
    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('documents', file);
      });

      const response = await apiClient.post(`/document/${projectId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading project documents for project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Upload the final signed PDF document with its integrity hash
   * @param {string} projectId - ID of the project
   * @param {Blob} pdfBlob - The final PDF blob
   * @param {string} fileName - File name for the PDF
   * @param {string} documentHash - SHA-256 hex hash of the PDF
   * @param {string|null} googleDocTitle - Optional Google Drive document title for backend file naming
   * @returns {Promise<Object>} - { finalDocument: { id, version, fileName, dropboxUrl, documentHash, downloadUrl, createdAt } }
   */
  uploadFinalDocument: async (projectId, pdfBlob, fileName, documentHash, googleDocTitle = null) => {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfBlob, fileName);
      formData.append('documentHash', documentHash);
      if (googleDocTitle) {
        formData.append('googleDocTitle', googleDocTitle);
      }
      const response = await apiClient.post(`/projects/${projectId}/final-documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading final document for project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Upload final document and apply digital certificate signature (.p12/.pfx).
   * @param {string} projectId
   * @param {Blob} pdfBlob
   * @param {string} fileName
   * @param {File|Blob} certificateFile
   * @param {string} certificatePassword
   * @param {Object} options
   * @param {string|null} options.documentHash
   * @param {string|null} options.signerDisplayName
   * @param {string|null} options.signerContactInfo
   * @param {string|null} options.signerTitle
   * @param {string|null} options.signatureReason
   * @param {string|null} options.signatureLocation
   * @param {Object|null} options.certificateAnchor
   * @param {Object|null} options.certificateAppearance
   * @param {string|null} options.googleDocTitle
   * @returns {Promise<Object>}
   */
  uploadFinalDocumentWithCertificate: async (
    projectId,
    pdfBlob,
    fileName,
    certificateFile,
    certificatePassword,
    options = {}
  ) => {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfBlob, fileName);
      formData.append('certificate', certificateFile, certificateFile?.name || 'signer-certificate.p12');
      formData.append('certificatePassword', certificatePassword || '');

      if (options.documentHash) formData.append('documentHash', options.documentHash);
      if (options.signerDisplayName) formData.append('signerDisplayName', options.signerDisplayName);
      if (options.signerContactInfo) formData.append('signerContactInfo', options.signerContactInfo);
      if (options.signerTitle) formData.append('signerTitle', options.signerTitle);
      if (options.signatureReason) formData.append('signatureReason', options.signatureReason);
      if (options.signatureLocation) formData.append('signatureLocation', options.signatureLocation);
      if (options.certificateAnchor) formData.append('certificateAnchor', JSON.stringify(options.certificateAnchor));
      if (options.certificateAppearance) formData.append('certificateAppearance', JSON.stringify(options.certificateAppearance));
      if (options.signatureAppearanceImage) formData.append('signatureAppearanceImage', options.signatureAppearanceImage);
      if (options.googleDocTitle) formData.append('googleDocTitle', options.googleDocTitle);

      const response = await apiClient.post(`/projects/${projectId}/final-documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading digitally signed final document for project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Step 1 of USB key signing: send PDF to backend, get back the content hash
   * that the hardware token must sign.
   */
  prepareSigning: async (projectId, pdfBlob, fileName, options = {}) => {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfBlob, fileName);
      if (options.signerDisplayName) formData.append('signerDisplayName', options.signerDisplayName);
      if (options.signerTitle)       formData.append('signerTitle', options.signerTitle);
      if (options.signerContactInfo) formData.append('signerContactInfo', options.signerContactInfo);
      if (options.signatureReason)   formData.append('signatureReason', options.signatureReason);
      if (options.signatureLocation) formData.append('signatureLocation', options.signatureLocation);
      if (options.certificateAnchor) formData.append('certificateAnchor', JSON.stringify(options.certificateAnchor));
      if (options.certificateAppearance) formData.append('certificateAppearance', JSON.stringify(options.certificateAppearance));
      if (options.signatureAppearanceImage) formData.append('signatureAppearanceImage', options.signatureAppearanceImage);

      const response = await apiClient.post(
        `/projects/${projectId}/final-documents/prepare-signing`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data; // { success, sessionId, contentHashHex }
    } catch (error) {
      console.error(`Error preparing signing for project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Step 2 of USB key signing: embed the hardware-token signature and upload.
   */
  embedSignature: async (projectId, sessionId, signatureHex, certificateDerHex, options = {}) => {
    try {
      const response = await apiClient.post(
        `/projects/${projectId}/final-documents/embed-signature`,
        {
          sessionId,
          signatureHex,
          certificateDerHex,
          documentHash: options.documentHash || undefined,
          googleDocTitle: options.googleDocTitle || undefined,
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error embedding signature for project ${projectId}:`, error);
      throw error;
    }
  },
});

export default documentService;
